import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, Moon, Sun } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/api";
import { normalizeWhatsApp } from "@/lib/phone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useBorrowerTheme } from "@/lib/borrowerTheme";

const PasswordInput = ({ testId, value, onChange, autoComplete }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input id={testId} data-testid={testId} type={visible ? "text" : "password"} value={value}
        onChange={onChange} required autoComplete={autoComplete}
        className="h-[44px] rounded-lg border-slate-300 bg-white pr-12 text-base text-slate-900" placeholder="••••••••" />
      <button type="button" aria-label={visible ? "Sembunyikan password" : "Tampilkan password"}
        onClick={() => setVisible((v) => !v)}
        className="absolute right-3 top-1/2 min-h-[44px] min-w-[44px] -translate-y-1/2 rounded-full p-1.5 text-slate-500 hover:text-slate-900">
        {visible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
};

export default function Login() {
  const { login, register } = useAuth();
  const { theme, toggleTheme } = useBorrowerTheme();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({
    name: "",
    email: "",
    whatsapp: "",
    password: "",
    password_confirm: "",
    registration_code: "",
  });
  const [whatsappError, setWhatsappError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && form.password !== form.password_confirm) {
      toast.error("Ulangi Password Baru harus sama dengan Password Baru");
      return;
    }
    let phone;
    if (mode === "register") {
      phone = normalizeWhatsApp(form.whatsapp);
      if (!phone) {
        const msg = "Masukkan nomor WhatsApp yang valid, misalnya 0812…, +62 812…, atau nomor internasional +….";
        setWhatsappError(msg);
        toast.error(msg);
        return;
      }
    }
    setLoading(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else {
        await register({
          name: form.name,
          email: form.email,
          phone,
          password: form.password,
          registration_code: form.registration_code,
        });
      }
      toast.success("Berhasil masuk");
      navigate("/");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="borrower-light login-page relative flex min-h-screen bg-[#C2D6EB] text-slate-900 max-lg:bg-[linear-gradient(to_bottom,#ffffff_75%,#C2D6EB_100%)]">
      <button type="button" onClick={toggleTheme} aria-label="Tema gelap"
        aria-pressed={theme === "dark"} title={theme === "dark" ? "Ganti ke tema terang" : "Ganti ke tema gelap"}
        className="absolute right-4 top-4 z-10 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0067c8]">
        {theme === "dark" ? <Moon className="h-5 w-5" aria-hidden="true" /> : <Sun className="h-5 w-5" aria-hidden="true" />}
      </button>
      <div className="hidden flex-1 flex-col justify-between bg-white p-14 lg:flex">
        <div className="mx-auto w-full max-w-lg text-left">
          <img src="/door-lock-sketch.png" alt="Sketsa kunci pintu keypad — akses storage memakai kode"
            className="mx-auto w-36 max-w-[170px]" loading="lazy" />
          <h1 className="font-heading mt-8 text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl">
            Tentukan Jadwal, Pilih Gear, dan Akses Gudang.
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-slate-500">
            Katalog gear dan ruangan YukNgaji: tentukan jadwal ambil &amp; kembali, submit booking, tunggu approval admin.
          </p>
        </div>
        <div className="flex items-center justify-center gap-3">
          <img src="/yn-logo.svg" alt="YukNgaji" className="brand-yn h-5 w-5 object-contain" />
          <span className="font-heading text-xs font-bold text-slate-900">DEXTER</span>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">• GEAR &amp; MAINTENANCE TRACKER</p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center p-6 lg:w-[520px]">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 rounded-lg border border-slate-200 bg-white p-6 sm:p-8 max-lg:rounded-none max-lg:border-0 max-lg:bg-transparent max-lg:p-0 max-lg:shadow-none" data-testid="auth-form">
          <div className="mb-8 lg:hidden">
            <div className="relative">
              <img src="/door-lock-sketch.png" alt="Sketsa kunci pintu keypad — akses storage memakai kode"
                className="mx-auto w-28 max-w-[120px]" loading="lazy" />
              <div className="absolute bottom-2 right-0 flex items-center gap-1.5">
                <img src="/yn-logo.svg" alt="YukNgaji" className="brand-yn h-5 w-5 object-contain" />
                <span className="font-heading text-xs font-bold text-slate-900">Dexter</span>
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">
              {mode === "login" ? "Masuk" : "Daftar Akun"}
            </h2>
            <p className="mt-2 text-sm text-slate-500">{mode === "login" ? "Gunakan email kamu." : "Gunakan email & whatsapp mu."}</p>
          </div>

          {mode === "register" && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" htmlFor="register-name-input">Nama</Label>
              <Input data-testid="register-name-input" id="register-name-input" value={form.name} onChange={set("name")} required
                autoComplete="name"
                className="h-[44px] rounded-lg border-slate-300 bg-white text-base text-slate-900" placeholder="Nama lengkap" />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" htmlFor="login-email-input">Email</Label>
            <Input data-testid="login-email-input" id="login-email-input" type="email" value={form.email} onChange={set("email")} required
              autoComplete="email" inputMode="email" autoCapitalize="none"
              className="h-[44px] rounded-lg border-slate-300 bg-white text-base text-slate-900" placeholder="nama@email.com" />
          </div>

          {mode === "register" && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" htmlFor="register-phone-input">No. WhatsApp</Label>
              <Input data-testid="register-phone-input" id="register-phone-input" value={form.whatsapp}
                onChange={(e) => { set("whatsapp")(e); setWhatsappError(""); }}
                required autoComplete="tel" inputMode="tel" maxLength={24}
                aria-invalid={!!whatsappError} aria-describedby="register-phone-hint"
                className="h-[44px] rounded-lg border-slate-300 bg-white text-base text-slate-900" placeholder="0812…" />
              <p id="register-phone-hint" className="text-[13px] text-slate-500">
                Boleh diawali 08, 628, atau +62. Nomor internasional harus diawali +.
              </p>
              {whatsappError && <p className="text-[13px] font-semibold text-rose-700">{whatsappError}</p>}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor={mode === "register" ? "register-password-input" : "login-password-input"} className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              {mode === "register" ? "Password Baru" : "Password"}
            </Label>
            <PasswordInput testId={mode === "register" ? "register-password-input" : "login-password-input"}
              value={form.password} onChange={set("password")}
              autoComplete={mode === "register" ? "new-password" : "current-password"} />
          </div>

          {mode === "register" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="register-password-confirm-input" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Ulangi Password Baru</Label>
                <PasswordInput testId="register-password-confirm-input" value={form.password_confirm}
                  onChange={set("password_confirm")} autoComplete="new-password" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500" htmlFor="register-secret-code-input">Kode Rahasia</Label>
                <Input data-testid="register-secret-code-input" id="register-secret-code-input" type="password" value={form.registration_code}
                  onChange={set("registration_code")} required autoComplete="off"
                  className="h-[44px] rounded-lg border-slate-300 bg-white text-base text-slate-900"
                  placeholder="Minta kode rahasianya ke Hasbi / Ghazy" />
              </div>
            </>
          )}

          <Button data-testid="auth-submit-button" type="submit" disabled={loading}
            className="h-[44px] w-full rounded-lg border border-[#0067c8] bg-[#0067c8] text-base font-semibold text-white hover:border-[#004f9e] hover:bg-[#004f9e]">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Masuk" : "Daftar"}
          </Button>

          <button type="button" data-testid="toggle-auth-mode"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="min-h-[44px] w-full text-sm font-medium text-slate-500 hover:text-slate-900 hover:underline">
            {mode === "login" ? "Belum punya akun? Daftar" : "Sudah punya akun? Masuk"}
          </button>
        </form>
      </div>
    </div>
  );
}
